import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
// 📊 이벤트 트래킹
import { trackPaymentComplete } from '../../utils/eventTracker';
import { getMetaMatchParams } from '../../utils/analytics';
import {
  createPurchaseEventId,
  trackMetaPurchasePixel,
} from '../../utils/paymentTracking';
import PaymentFlowStatus from '../../components/PaymentFlowStatus';
import { resolvePaymentCouponCode } from '../../utils/paymentPromo';

const BillingSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const { fetchSubscriptionStatus } = useSubscription();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState(null);
  
  // 중복 실행 방지를 위한 ref들
  const hasProcessedRef = useRef(false);
  const processingRef = useRef(false);
  const mountedRef = useRef(true);

  // API 기본 URL 설정
  const getApiBaseUrl = () => {
    const currentUrl = window.location.hostname;
    if (currentUrl.includes('localhost')) {
      return 'http://localhost:8000';
    } else if (currentUrl.includes('whatquiztest')) {
      return 'https://quiz-api-test-397277209875.us-central1.run.app';
    } else {
      return 'https://quiz-api-397277209875.us-central1.run.app';
    }
  };

  useEffect(() => {
    // 컴포넌트 마운트 상태 설정
    mountedRef.current = true;
    
    const processBillingKey = async () => {
      // 이미 처리했거나 현재 처리 중이면 무시
      if (hasProcessedRef.current || processingRef.current) {
        return;
      }
      
      // 처리 중 플래그 설정
      processingRef.current = true;
      
      try {
        // URL 파라미터에서 authKey와 customerKey 가져오기
        const authKey = searchParams.get('authKey');
        const customerKey = searchParams.get('customerKey');
        const amount = searchParams.get('amount');
        const planId = searchParams.get('planId');
        const couponCode = searchParams.get('coupon'); // 쿠폰 코드
        const promoCode = searchParams.get('promo'); // 프로모션 코드
        
        const finalCouponCode = resolvePaymentCouponCode(couponCode, promoCode);

        if (!authKey || !customerKey) {
          throw new Error('필수 파라미터가 누락되었습니다.');
        }

        if (!currentUser) {
          throw new Error('로그인이 필요합니다.');
        }

        // 백엔드로 빌링키 발급 요청
        const API_BASE_URL = getApiBaseUrl();
        const idToken = await currentUser.getIdToken(true);

        const response = await fetch(`${API_BASE_URL}/api/payment/billing/issue`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({
            authKey: authKey,
            customerKey: customerKey
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || '빌링키 발급에 실패했습니다.');
        }

        const result = await response.json();

        // 첫 결제가 필요한 경우 (amount와 planId가 있는 경우)
        if (amount && planId) {
          // 처리 완료 플래그 설정 (빌링키 발급 성공 후)
          hasProcessedRef.current = true;
          
          // 백엔드에서 주문번호 생성
          const orderResponse = await fetch(`${API_BASE_URL}/api/payment/create-order`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({
              plan_id: planId
            })
          });

          if (!orderResponse.ok) {
            const orderError = await orderResponse.json();
            throw new Error(orderError.detail || '주문번호 생성에 실패했습니다.');
          }

          const orderResult = await orderResponse.json();
          const orderId = orderResult.data.order_id;
          const purchaseEventId = createPurchaseEventId(orderId);
          
          // 빌링키로 첫 결제 실행
          const paymentResponse = await fetch(`${API_BASE_URL}/api/payment/billing/pay`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({
              billing_key: result.data.billing_key,
              customer_key: customerKey,
              order_id: orderId,
              order_name: '왓퀴즈 PRO 이용권',
              amount: parseInt(amount),
              plan_id: planId,
              event_id: purchaseEventId,
              ...(finalCouponCode && { coupon_code: finalCouponCode }),
              ...getMetaMatchParams(),
            })
          });

          if (!paymentResponse.ok) {
            const errorData = await paymentResponse.json();
            
            // 중복 결제 에러 처리
            if (errorData.detail?.includes('이미 처리된 주문')) {
              // 이미 처리된 주문이면 성공 페이지로 이동
              if (mountedRef.current) {
                setTimeout(() => {
                  navigate('/payment/success', { 
                    state: { 
                      orderId: orderId,
                      orderNo: orderId,
                      amount: parseInt(amount),
                      paymentKey: 'duplicate_prevented',
                      planId: planId,
                      success: true,
                      message: '이미 처리된 결제입니다.'
                    }
                  });
                }, 1000);
              }
              return;
            }
            
            throw new Error(errorData.detail || '첫 결제 처리에 실패했습니다.');
          }

          const paymentResult = await paymentResponse.json();
          
          const actualAmount = paymentResult.data?.amount || paymentResult.data?.total_amount || parseInt(amount);
          
          // 📊 자체 결제 완료 이벤트 트래킹 (Firestore)
          trackPaymentComplete(orderId, planId, actualAmount, promoCode || null, null);
          trackMetaPurchasePixel({
            amount: actualAmount,
            eventId: paymentResult.data?.meta_event_id || purchaseEventId,
            orderId,
            planId,
          });

          // 결제 성공 → 구독 상태 강제 갱신 (캐시 무시)
          try {
            await fetchSubscriptionStatus(true);
          } catch (_e) { /* 갱신 실패해도 결제 성공 페이지로 이동 */ }

          // 결제 성공 페이지로 이동 (컴포넌트가 마운트되어 있을 때만)
          if (mountedRef.current) {
            setTimeout(() => {
              navigate('/payment/success', { 
                state: { 
                  orderId: orderId,
                  orderNo: orderId,
                  amount: parseInt(amount),
                  paymentKey: paymentResult.data.payment_key,
                  planId: planId,
                  success: true,
                  message: 'PRO 이용권이 활성화되었습니다!'
                }
              });
            }, 2000);
          }
        } else {
          // 빌링키만 등록한 경우
          hasProcessedRef.current = true;
          try {
            await fetchSubscriptionStatus(true);
          } catch (_e) { /* ignore */ }
          if (mountedRef.current) {
            setTimeout(() => {
              navigate('/subscription', { 
                state: { message: '카드가 성공적으로 등록되었습니다.' }
              });
            }, 2000);
          }
        }

      } catch (error) {
        console.error('빌링키 발급 오류:', error);
        if (mountedRef.current) {
          setError(error.message);
        }
      } finally {
        processingRef.current = false;
        if (mountedRef.current) {
          setIsProcessing(false);
        }
      }
    };

    // 필수 파라미터가 있을 때만 처리 시작
    const authKey = searchParams.get('authKey');
    const customerKey = searchParams.get('customerKey');
    
    if (authKey && customerKey && currentUser) {
      processBillingKey();
    } else if (!currentUser) {
      setError('로그인이 필요합니다.');
      setIsProcessing(false);
    } else {
      setError('필수 파라미터가 누락되었습니다.');
      setIsProcessing(false);
    }

    // 컴포넌트 언마운트 시 정리
    return () => {
      mountedRef.current = false;
    };
  }, [currentUser, navigate, searchParams, fetchSubscriptionStatus]);

  const isPaymentOutcomePending = Boolean(
    error
    && (
      error.includes('결제 결과를 확인')
      || error.includes('다시 결제하지')
      || error.includes('이전 결제 결과')
    )
  );

  if (isProcessing) {
    return (
      <PaymentFlowStatus
        variant="processing"
        title="카드 정보를 확인하고 있어요"
        description="카드 등록과 PRO 활성화를 진행 중이에요. 이 화면을 닫지 말아주세요."
      />
    );
  }

  if (error) {
    return (
      <PaymentFlowStatus
        variant={isPaymentOutcomePending ? 'pending' : 'error'}
        title={isPaymentOutcomePending ? '결제 결과를 확인하고 있어요' : '카드를 등록하지 못했어요'}
        description={
          isPaymentOutcomePending
            ? error
            : '카드 등록을 완료하지 못했어요. 잠시 후 다시 시도해주세요.'
        }
        detailCode={!isPaymentOutcomePending ? error : undefined}
        primaryAction={{
          label: isPaymentOutcomePending ? '결제 내역 확인하기' : '카드 다시 등록하기',
          onClick: () => navigate(
            isPaymentOutcomePending ? '/subscription' : '/subscription/pro'
          ),
        }}
      />
    );
  }

  return (
    <PaymentFlowStatus
      variant="success"
      title="카드를 등록했어요"
      description="선택한 기간 동안 PRO 기능을 이용할 수 있어요. 잠시 후 구독 관리로 이동해요."
      primaryAction={{
        label: '구독 관리로 이동하기',
        onClick: () => navigate('/subscription'),
      }}
    />
  );
};

export default BillingSuccess; 